/**
 * AI Chat Input Component
 * Text input with send button for the AI chat interface.
 */

import React, { useRef, useEffect } from 'react';
import { Send, Loader2, Paperclip, X, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import type { AIAttachment } from '@/types/ai-assistant';

const ACCEPTED_FILES = '.pdf,.docx,.xlsx,.xlsm,.txt,.csv,.md,.json,.html';

interface AIChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  isLoading: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  attachments?: AIAttachment[];
  isUploading?: boolean;
  onAttach?: (file: File) => void;
  onRemoveAttachment?: (id: number) => void;
}

export function AIChatInput({
  value,
  onChange,
  onSend,
  isLoading,
  disabled = false,
  placeholder = "Ask me anything about your practice...",
  className,
  attachments = [],
  isUploading = false,
  onAttach,
  onRemoveAttachment,
}: AIChatInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 150)}px`;
    }
  }, [value]);
  
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (value.trim() && !isLoading && !disabled) {
        onSend();
      }
    }
  };
  
  const canSend = value.trim() && !isLoading && !disabled && !isUploading;

  return (
    <div className={cn("border-t bg-background", className)}>
      {(attachments.length > 0 || isUploading) && (
        <div className="flex flex-wrap gap-2 px-4 pt-3">
          {attachments.map((a) => (
            <span
              key={a.id}
              className="flex items-center gap-1.5 rounded-full border bg-muted px-3 py-1 text-xs"
            >
              <FileText className="h-3 w-3 shrink-0" />
              <span className="max-w-[180px] truncate">{a.filename}</span>
              {onRemoveAttachment && (
                <button onClick={() => onRemoveAttachment(a.id)} aria-label={`Remove ${a.filename}`}>
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
          {isUploading && (
            <span className="flex items-center gap-1.5 rounded-full border bg-muted px-3 py-1 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> Uploading…
            </span>
          )}
        </div>
      )}
      <div className="relative flex items-end gap-2 p-4 pt-3">
      {onAttach && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_FILES}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onAttach(file);
              e.target.value = '';
            }}
          />
          <Button
            variant="outline"
            size="icon"
            className="h-10 w-10 rounded-full shrink-0"
            disabled={isLoading || disabled || isUploading}
            onClick={() => fileInputRef.current?.click()}
            aria-label="Attach a file"
          >
            <Paperclip className="h-4 w-4" />
          </Button>
        </>
      )}
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={isLoading || disabled}
        className={cn(
          "min-h-[44px] max-h-[150px] resize-none pr-12",
          "rounded-2xl border-input",
          "focus-visible:ring-1 focus-visible:ring-ring"
        )}
        rows={1}
      />
      <Button
        onClick={onSend}
        disabled={!canSend}
        size="icon"
        aria-label="Send message"
        className={cn(
          "h-10 w-10 rounded-full shrink-0 transition-colors",
          canSend
            ? "bg-primary hover:bg-primary/90"
            : "bg-muted text-muted-foreground disabled:opacity-100"
        )}
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Send className="h-4 w-4" />
        )}
      </Button>
      </div>
    </div>
  );
}
